import {getAllDevices, getUsersDb} from "../../utils/authService"
import React, {Fragment, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useAuth0} from "@auth0/auth0-react";

const Users = () => {
    const isAdmin = useSelector(state => state.user.isAdmin);
    const userStatus = useSelector(state => state.user.status);
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [devices, setDevices] = useState([]);
    const [deletingDevice, setDeletingDevice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filteredUsers, setFilteredUsers] = useState([]); // Store filtered users
    const [searchQuery, setSearchQuery] = useState(""); // Search input state
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [selectedUserGroupId, setSelectedUserGroupId] = useState("");
    const [selectedUserForDevice, setSelectedUserForDevice] = useState(null);
    const {getAccessTokenSilently} = useAuth0();
    useEffect(() => {
        // wait until the user's permissions are loaded, otherwise a page reload always redirects
        if (userStatus === "idle" || userStatus === "loading") return;

        if (!isAdmin) {
            navigate("/dashboard");
            return;
        }

        fetchUsers();
        fetchDevices();
    }, [isAdmin, userStatus, navigate]);
    const fetchDevices = async () => {
        try {
            const token = await getAccessTokenSilently();
            const data = await getAllDevices(token);
            if (data) {
                setDevices(data);
            } else {
                setError("Failed to fetch devices");
            }
        } catch (err) {
            setError("Failed to fetch devices");
        } finally {
            setLoading(false);
        }
    };
    const fetchUsers = async () => {
        try {
            const token = await getAccessTokenSilently();
            const data = await getUsersDb(token);
            if (data) {
                setUsers(data);
                setFilteredUsers(data);
            } else {
                setError("Failed to fetch users");

            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const lowerCaseQuery = searchQuery.toLowerCase();
        const filtered = users.filter(user =>
            user.Email.toLowerCase().includes(lowerCaseQuery) ||
            user.UserGroup?.Devices?.find(device => device.MacAddress.toLowerCase().includes(lowerCaseQuery))
        );
        setFilteredUsers(filtered);
    }, [searchQuery, users]);

    const lowerCaseQuery = searchQuery.toLowerCase();
    const filteredDevices = devices.filter(device =>
        device.macAddress.toLowerCase().includes(lowerCaseQuery) ||
        device.name.toLowerCase().includes(lowerCaseQuery) ||
        device.ownerEmails.some(email => email.toLowerCase().includes(lowerCaseQuery))
    );

    const deleteDevice = async (device) => {
        const owner = device.ownerEmails.length > 0 ? device.ownerEmails.join(", ") : "no owner";
        if (!window.confirm(`Delete device ${device.name} (${device.macAddress}) owned by ${owner}? This also removes the claim on the device.`)) {
            return;
        }

        setDeletingDevice(device.macAddress);
        try {
            const token = await getAccessTokenSilently();
            const response = await fetch(`/api/devices/${device.macAddress}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            if (!response.ok) throw new Error("Failed to delete device");
            await Promise.all([fetchDevices(), fetchUsers()]);
        } catch (error) {
            console.error("Error deleting device:", error);
            alert("Error deleting device");
        } finally {
            setDeletingDevice(null);
        }
    };

    const transferDevice = async (deviceId, fromUser, newUserGroupId) => {
        try {
            const token = await getAccessTokenSilently();
            const response = await fetch(`/api/devices/${deviceId}/transfer`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({FromUserGroupId: fromUser, ToUserGroupId: newUserGroupId}),
            });

            if (!response.ok) throw new Error("Failed to transfer device");
            alert("Device transferred successfully!");
            await fetchUsers();
            setSelectedDevice(null); // Close the transfer UI
            setSelectedUser(null); // Close the modal after transfer
        } catch (error) {
            console.error("Error transferring device:", error);
            alert("Error transferring device.");
        }
    };

    const deleteUser = async (user) => {
        try{
            const token = await getAccessTokenSilently();
            const response = await fetch(`/api/user/${user.Id}`,{
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            if (!response.ok) throw new Error("Failed to delete user");
            alert("User deleted successfully!");
            await fetchUsers();
        }
        catch (error) {
            console.error("Error deleting user:", error);
            alert("Error deleting user");
        }
    };

    if (!isAdmin) return null;

    return (
        <Fragment>
            <div className="container mx-auto p-6">
                <h1 className="text-3xl font-semibold text-gray-800 mb-6">Users</h1>

                {loading && <p className="text-center text-gray-500">Loading...</p>}
                {error && <p className="text-center text-red-500 font-semibold">Error: {error}</p>}

                {!loading && !error && (
                    <>
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Search by email, device name or MAC address..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-dol-blue focus:outline-none"
                            />
                        </div>

                        <div className="overflow-x-auto rounded-lg shadow-md">
                            <table className="w-full border border-gray-200 bg-white rounded-lg">
                                <thead className="bg-dol-blue text-white">
                                <tr>
                                    <th className="p-3 text-left">Email</th>
                                    <th className="p-3 text-left">Id</th>
                                    <th className="p-3 text-left">User Group Id</th>
                                    <th className="p-3 text-center">Devices</th>
                                    <th className="p-3 text-center">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map((user) => (
                                        <tr key={user.Id} className="border-b hover:bg-gray-50">
                                            <td className="p-3">{user.Email}</td>
                                            <td className="p-3">{user.Id}</td>
                                            <td className="p-3">{user.UserGroupId}</td>
                                            <td className="p-3 text-center">
                                                {user.UserGroup?.Devices?.length > 0 && (
                                                    <button
                                                        onClick={() => setSelectedUser(user)}
                                                        className="bg-dol-blue text-white px-4 py-1 rounded-lg hover:bg-blue-600 transition"
                                                    >
                                                        View Devices
                                                    </button>
                                                )}
                                            </td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={() => deleteUser(user)}
                                                    className="bg-red-500 text-white px-4 py-1 rounded-lg hover:bg-red-600 transition"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="p-4 text-center text-gray-500">
                                            No users found.
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        <h2 className="text-2xl font-semibold text-gray-800 mt-10 mb-4">Devices</h2>
                        <div className="overflow-x-auto rounded-lg shadow-md">
                            <table className="w-full border border-gray-200 bg-white rounded-lg">
                                <thead className="bg-dol-blue text-white">
                                <tr>
                                    <th className="p-3 text-left">Name</th>
                                    <th className="p-3 text-left">MAC Address</th>
                                    <th className="p-3 text-left">Type</th>
                                    <th className="p-3 text-left">Owner</th>
                                    <th className="p-3 text-left">Claimed At</th>
                                    <th className="p-3 text-center">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredDevices.length > 0 ? (
                                    filteredDevices.map((device) => (
                                        <tr key={device.macAddress} className="border-b hover:bg-gray-50">
                                            <td className="p-3">{device.name}</td>
                                            <td className="p-3">{device.macAddress}</td>
                                            <td className="p-3">{device.deviceType}</td>
                                            <td className="p-3">
                                                {device.ownerEmails.length > 0 ? (
                                                    device.ownerEmails.join(", ")
                                                ) : (
                                                    <span className="text-orange-600 font-medium">No owner</span>
                                                )}
                                            </td>
                                            <td className="p-3">{new Date(device.timeClaimed).toDateString()}</td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={() => deleteDevice(device)}
                                                    disabled={deletingDevice !== null}
                                                    className="bg-red-500 text-white px-4 py-1 rounded-lg hover:bg-red-600 transition disabled:opacity-50"
                                                >
                                                    {deletingDevice === device.macAddress ? "Deleting..." : "Delete"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="p-4 text-center text-gray-500">
                                            No devices found.
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Device Modal */}
            {selectedUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
                    <div className="bg-slate-100 p-6 rounded-lg shadow-lg w-1/2 relative">
                        <h2 className="text-xl font-semibold mb-4">
                            Devices for {selectedUser.Email}
                        </h2>
                        <button
                            className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded"
                            onClick={() => setSelectedUser(null)}
                        >
                            Close
                        </button>

                        {selectedUser.UserGroup?.Devices?.length > 0 ? (
                            <div className="flex flex-col items-center justify-center min-h-0 mb-4">
                                {selectedUser.UserGroup.Devices.map((device) => (
                                    <div key={device.Id}
                                         className="w-full py-8 mx-auto bg-white rounded-lg shadow-xl md:w-1/2 mb-3">
                                        <div className="max-w-sm px-4 mx-auto space-y-2">
                                            {/* Header with Device Status */}
                                            <div className="flex justify-between">
                                                <h2 className="text-2xl font-bold text-dol-blue">{device.Name}</h2>
                                            </div>

                                            {/* Device Details */}
                                            <div className='space-y-2'>
                                                <ul>
                                                    <li className="flex">
                                                        <p className="w-5/12 font-medium">Device Type:</p>
                                                        <p>{device.DeviceType}</p>
                                                    </li>
                                                    <li className="flex">
                                                        <p className="w-5/12 font-medium">MAC Address:</p>
                                                        <p>{device.MacAddress}</p>
                                                    </li>
                                                    <li className="flex">
                                                        <p className="w-5/12 font-medium">Claimed At:</p>
                                                        <p>{new Date(device.TimeClaimed).toDateString()}</p>
                                                    </li>
                                                </ul>

                                                {/* Buttons Section */}

                                            </div>
                                        </div>
                                        <div className="flex justify-end pt-3 max-w-xl mx-auto">
                                            <button
                                                className="mr-2 bg-dol-blue text-white px-4 py-2 rounded-lg"
                                                onClick={() => setSelectedDevice(device)}
                                            >
                                                Transfer
                                            </button>

                                        </div>
                                    </div>
                                ))}
                            </div>) : (
                            <p>No devices found.</p>
                        )}


                        {/* Transfer UI */}
                        {selectedDevice && (
                            <div className="mt-4 border-t pt-4">
                                <h3 className="text-lg font-semibold">Transfer Device</h3>
                                <p><strong>Device:</strong> {selectedDevice.Name}</p>
                                <select
                                    className="mt-2 w-full border p-2 rounded"
                                    value={selectedUserGroupId}
                                    onChange={(e) => setSelectedUserGroupId(e.target.value)}
                                >
                                    <option value="">Select a user</option>
                                    {users.map((user) => (
                                        <option key={user.UserGroupId} value={user.UserGroupId}>
                                            {user.Email}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className="mt-2 bg-green-500 text-white px-3 py-1 rounded"
                                    onClick={() => {
                                        if (selectedUserGroupId) {
                                            transferDevice(selectedDevice.MacAddress, selectedDevice.UserGroupId, selectedUserGroupId);
                                        }
                                    }}
                                >
                                    Confirm Transfer
                                </button>

                                <button
                                    className="mt-2 ml-2 bg-gray-500 text-white px-3 py-1 rounded"
                                    onClick={() => setSelectedDevice(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}


                    </div>
                </div>
            )}

        </Fragment>
    );
};
export default Users;